const BIOMETRIC_PREF_KEY = 'alea.biometric.login.enabled';

function biometricVaultServer(): string {
  const domain = String(
    import.meta.env.VITE_AUTH_EMAIL_DOMAIN || import.meta.env.VITE_WEBAUTHN_RP_ID || '',
  ).trim();
  if (!domain) {
    throw new Error('VITE_AUTH_EMAIL_DOMAIN or VITE_WEBAUTHN_RP_ID is required for biometric vault.');
  }
  return domain;
}

export interface BiometricAvailability {
  available: boolean;
  label: string;
}

async function isWebAuthnAvailable(): Promise<boolean> {
  const { isWebAuthnPlatformAvailable } = await import('./devicePasskeys.js');
  return isWebAuthnPlatformAvailable();
}

type NativeBiometricModule = {
  BiometryType: {
    FACE_ID: number;
    FACE_AUTHENTICATION: number;
    IRIS_AUTHENTICATION: number;
  };
  NativeBiometric: {
    isAvailable: () => Promise<{ isAvailable: boolean; biometryType: number }>;
    setCredentials: (opts: { username: string; password: string; server: string }) => Promise<void>;
    verifyIdentity: (opts: { reason: string; title: string }) => Promise<{ verified: boolean }>;
    getCredentials: (opts: { server: string }) => Promise<{ username: string; password: string }>;
    deleteCredentials: (opts: { server: string }) => Promise<void>;
  };
};

async function loadNativeBiometricModule(): Promise<NativeBiometricModule | null> {
  try {
    const pkg = '@capgo/capacitor-native-biometric';
    const loader = new Function('p', 'return import(p)') as (p: string) => Promise<NativeBiometricModule>;
    return await loader(pkg);
  } catch {
    return null;
  }
}

async function isNativeBiometricAvailable(): Promise<BiometricAvailability> {
  try {
    const mod = await loadNativeBiometricModule();
    if (!mod) return { available: false, label: '' };
    const { isAvailable, biometryType } = await mod.NativeBiometric.isAvailable();
    const { BiometryType } = mod;
    if (!isAvailable) return { available: false, label: '' };
    const label = biometryType === BiometryType.FACE_ID
      ? 'Face ID'
      : biometryType === BiometryType.FACE_AUTHENTICATION
        ? 'Face unlock'
        : biometryType === BiometryType.IRIS_AUTHENTICATION
          ? 'Iris'
          : 'Fingerprint';
    return { available: true, label };
  } catch {
    return { available: false, label: '' };
  }
}

export async function getBiometricAvailability(): Promise<BiometricAvailability> {
  const native = await isNativeBiometricAvailable();
  if (native.available) return native;
  const web = await isWebAuthnAvailable();
  return {
    available: web,
    label: web ? 'Passkey' : '',
  };
}

export function isBiometricLoginEnabled(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(BIOMETRIC_PREF_KEY) === '1';
}

export function setBiometricLoginEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  if (enabled) localStorage.setItem(BIOMETRIC_PREF_KEY, '1');
  else localStorage.removeItem(BIOMETRIC_PREF_KEY);
}

/**
 * Native: store password vault unlocked by biometrics (local unlock only — not device trust).
 * Web: register a platform passkey used for device trust.
 */
export async function saveBiometricCredentials(username: string, password: string): Promise<void> {
  const native = await isNativeBiometricAvailable();
  if (native.available) {
    const mod = await loadNativeBiometricModule();
    if (!mod) throw new Error('Biometric storage is not available on this device.');
    const { NativeBiometric } = mod;
    await NativeBiometric.setCredentials({
      username,
      password,
      server: biometricVaultServer(),
    });
    setBiometricLoginEnabled(true);
    return;
  }

  const { registerPasskeyOnDevice } = await import('./devicePasskeys.js');
  const result = await registerPasskeyOnDevice();
  if (result && 'skipped' in result && result.skipped) {
    throw new Error('Passkey registration is not available on this device.');
  }
  setBiometricLoginEnabled(true);
}

export async function loadBiometricCredentials(): Promise<{ username: string; password: string } | null> {
  if (!isBiometricLoginEnabled()) return null;

  const native = await isNativeBiometricAvailable();
  if (native.available) {
    const mod = await loadNativeBiometricModule();
    if (!mod) throw new Error('Biometric storage is not available on this device.');
    const { NativeBiometric } = mod;
    const verified = await NativeBiometric.verifyIdentity({
      reason: 'Unlock your session',
      title: 'Sign in',
    });
    if (!verified.verified) return null;
    const creds = await NativeBiometric.getCredentials({ server: biometricVaultServer() });
    return { username: creds.username, password: creds.password };
  }

  // Web passkeys use a dedicated passwordless sign-in path (see tryBiometricSignIn).
  return null;
}

/** True when this device can offer biometric / passkey unlock on the login screen. */
export async function canOfferBiometricLogin(): Promise<BiometricAvailability> {
  if (!isBiometricLoginEnabled()) return { available: false, label: '' };

  const native = await isNativeBiometricAvailable();
  if (native.available) return native;

  const web = await isWebAuthnAvailable();
  if (!web) return { available: false, label: '' };

  const { getStoredCredentialHint } = await import('./devicePasskeys.js');
  if (!getStoredCredentialHint()) return { available: false, label: '' };

  return { available: true, label: 'Passkey' };
}

export async function clearBiometricCredentials(): Promise<void> {
  setBiometricLoginEnabled(false);
  try {
    const native = await isNativeBiometricAvailable();
    if (native.available) {
      const mod = await loadNativeBiometricModule();
      if (mod) {
        const { NativeBiometric } = mod;
        await NativeBiometric.deleteCredentials({ server: biometricVaultServer() });
      }
    }
  } catch {
    // Best-effort cleanup
  }
}

/**
 * Confirms the person at the device without signing in again.
 * Web uses a short passkey assertion when possible; native uses local biometrics.
 */
export async function verifyDeviceIdentity(
  reason = 'Confirm your identity to continue navigation',
  title = 'Unlock route',
): Promise<boolean> {
  const native = await isNativeBiometricAvailable();
  if (native.available) {
    const mod = await loadNativeBiometricModule();
    if (!mod) return false;
    try {
      const result = await mod.NativeBiometric.verifyIdentity({ reason, title });
      return Boolean(result.verified);
    } catch {
      return false;
    }
  }

  try {
    const { authenticatePasskeyOnDevice, getStoredCredentialHint } = await import('./devicePasskeys.js');
    if (!getStoredCredentialHint() && !isBiometricLoginEnabled()) return false;
    await authenticatePasskeyOnDevice();
    return true;
  } catch {
    return false;
  }
}
